import { Badge, Box, Collapse, Paper, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ReactNode, useEffect, useState } from "react";
import IconButton from "./IconButton";
import { useIntl } from "../../contexts/IntlContext";
import { fontWeights } from "../../themes/fontWeights";

export type FiltersBarRenderState = { compact: boolean; open: boolean };

type FiltersBarProps = {
  activeFilterCount: number;
  children: ReactNode | ((state: FiltersBarRenderState) => ReactNode);
  sticky?: boolean;
  top?: number;
};

export default function FiltersBar({
  activeFilterCount,
  children,
  sticky = true,
  top = 74,
}: FiltersBarProps) {
  const intl = useIntl();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(!isSmall);

  useEffect(() => {
    setOpen(!isSmall);
  }, [isSmall]);

  const compact = isSmall && !open;
  const content = typeof children === "function" ? children({ compact, open }) : children;

  return (
    <Paper
      elevation={1}
      sx={{
        position: sticky ? "sticky" : "static",
        top: sticky ? top : undefined,
        zIndex: sticky ? (theme) => theme.zIndex.drawer : undefined,
        p: compact ? 1 : 2,
        mb: compact ? 0 : 2,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: compact ? 0.5 : 2,
          flexWrap: "wrap",
          "& .MuiFormControl-root, & .MuiFormControlLabel-root, & .MuiAutocomplete-root": {
            marginTop: 0,
            marginBottom: 0,
          },
        }}
      >
        {isSmall && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Badge
              badgeContent={activeFilterCount}
              color="primary"
              invisible={open || activeFilterCount === 0}
            >
              <IconButton size="small" onClick={() => setOpen(s => !s)} aria-label="Filters">
                {open ? <CloseIcon /> : <FilterListIcon />}
              </IconButton>
            </Badge>

            {!open && (
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: fontWeights.medium, textAlign: "right", mr: 1, lineHeight: 1 }}
              >
                {intl.formatMessage({ id: "filters.title" })}
              </Typography>
            )}
          </Box>
        )}

        <Collapse in={open} orientation="vertical" sx={{ flex: 1, width: "100%" }}>
          {content}
        </Collapse>
      </Box>
    </Paper>
  );
}
